// router.tsx — единая точка сборки routes; экспортирует non-component
// (router), поэтому fast-refresh на него не работает — это норма.
/* eslint-disable react-refresh/only-export-components */
import {
  createRouter,
  createRootRoute,
  createRoute,
  redirect,
  Outlet,
  useMatchRoute,
} from '@tanstack/react-router';
import { useAuthStore } from './store/auth';
import { signup, requestPasswordReset, confirmPasswordReset } from './api/auth';
import { getApiClient } from './api/factory';
import { LoginView } from './pages/LoginView';
import { ForgotPasswordView } from './pages/ForgotPasswordView';
import { ResetPasswordView } from './pages/ResetPasswordView';
import { SignupView } from './pages/SignupView';
import { OnboardingView } from './pages/OnboardingView';
import { ForcedEnrollmentView } from './pages/ForcedEnrollmentView';
import { ConversationView } from './pages/ConversationView';
import { SitesPage } from './pages/SitesPage';
import { SettingsPage } from './pages/SettingsPage';
import { WebhooksPage } from './pages/WebhooksPage';
import { CannedResponsesPage } from './pages/CannedResponsesPage';
import { ExportsPage } from './pages/ExportsPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { LiveVisitorsPage } from './pages/LiveVisitorsPage';
import { ReportsPage } from './pages/ReportsPage';
import { BillingPage } from './pages/BillingPage';
import { StaffLoginView } from './pages/staff/StaffLoginView';
import { StaffShell } from './pages/staff/StaffShell';
import { StaffDashboard } from './pages/staff/StaffDashboard';
import { StaffTenantsPage } from './pages/staff/StaffTenantsPage';
import { StaffTenantDetail } from './pages/staff/StaffTenantDetail';
import { useStaffAuthStore } from './store/staffAuth';
import { lazy, Suspense, useState } from 'react';
import { useT } from './i18n';
import { useQuery } from '@tanstack/react-query';
import { listConversations } from './api/conversations';
import { Sidebar } from './components/Sidebar';
import { OfflineBanner } from './components/OfflineBanner';
import { UpdateBanner } from './components/UpdateBanner';
import { ConversationsRail } from './components/ConversationsRail';
import { InboxEmpty } from './components/InboxEmpty';
import { ShortcutsDialog } from './components/ShortcutsDialog';
import { PaywallDialog } from './components/PaywallDialog';
import { useHotkeys } from './hooks/useHotkeys';
import { useMessageNotifier } from './hooks/useMessageNotifier';
import { useUnreadBadge } from './hooks/useUnreadBadge';
import { useDesktopDeepLink } from './hooks/useDesktopDeepLink';
import { useDesktopAuthSync } from './hooks/useDesktopAuthSync';
import { useServerLocaleSync } from './hooks/useServerLocaleSync';

// recharts весит ~200 КБ gzip — выносим analytics в отдельный chunk,
// чтобы основные страницы (диалоги, sites, settings) грузились быстро.
const AnalyticsPage = lazy(() =>
  import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
);

function AppShell() {
  // ANX-044 — глобальный `?` открывает модал со списком шорткатов.
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  useHotkeys({
    '?': () => setShortcutsOpen(true),
  });
  // ANX-053 — звук + browser-notification на новое сообщение visitor'а.
  useMessageNotifier();
  // ANX-059 — tray-badge с количеством открытых диалогов + deep-link маршрутизация в desktop.
  useUnreadBadge();
  useDesktopDeepLink();
  // ANX-059-followup — синхронизация JWT с Rust-поллером для background notifications.
  useDesktopAuthSync();
  // ANX-036 — применяет язык оператора с backend (cross-device preference).
  useServerLocaleSync();
  return (
    <div className="flex h-screen bg-slate-50 text-slate-900">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <OfflineBanner />
        {/* ANX-092 — баннер обновления desktop: установка только по клику. */}
        <UpdateBanner />
        <main className="min-h-0 flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      {/* ANX-105 — глобальный апселл при 402-лимите тарифа. */}
      <PaywallDialog />
    </div>
  );
}

// Inbox-layout: левый rail со списком диалогов + правая колонка с
// текущим диалогом (или placeholder, если ни один не выбран).
// Один общий Rail переживает навигацию между диалогами — Outlet
// перерисовывается, Rail остаётся.
function InboxLayout() {
  return (
    <div className="flex h-full min-h-0 bg-slate-50">
      <ConversationsRailWithActive />
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

function ConversationsRailWithActive() {
  // Активный диалог берём через `useMatchRoute` — у `useParams` со страницы
  // index ('/') не будет id, а strict-вариант кинет ошибку.
  const matchRoute = useMatchRoute();
  const m = matchRoute({ to: '/conversations/$id' });
  const activeId = m ? m.id : undefined;
  return <ConversationsRail activeId={activeId} />;
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    if (useAuthStore.getState().session !== null) {
      throw redirect({ to: '/' });
    }
  },
  component: function LoginRoute() {
    return (
      <LoginView
        onAuthenticated={async () => {
          await router.navigate({ to: '/' });
        }}
        onEnrollmentRequired={async (enrollmentSession) => {
          await router.navigate({
            to: '/onboarding/passkey-required',
            search: { session: enrollmentSession },
          });
        }}
      />
    );
  },
});

// ANX-102 — публичный запрос сброса пароля. Если уже залогинен, ведём на /.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  beforeLoad: () => {
    if (useAuthStore.getState().session !== null) {
      throw redirect({ to: '/' });
    }
  },
  component: function ForgotPasswordRoute() {
    return (
      <ForgotPasswordView
        onSubmit={async (input) => {
          await requestPasswordReset(getApiClient(), input);
        }}
      />
    );
  },
});

// ANX-102 — установка нового пароля по токену из письма. URL формирует
// worker как ${PANEL_URL}/reset-password/{token} (jobs/password_reset_email.go),
// токен hex → один path-сегмент. Доступен без редиректа залогиненных:
// ссылку могли открыть на устройстве со старой сессией.
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password/$token',
  component: function ResetPasswordRoute() {
    const { token } = resetPasswordRoute.useParams();
    return (
      <ResetPasswordView
        onSubmit={async (password) => {
          await confirmPasswordReset(getApiClient(), { token, password });
        }}
      />
    );
  },
});

// Public /signup — ANX-024. Если уже залогинен, ведём на /.
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  beforeLoad: () => {
    if (useAuthStore.getState().session !== null) {
      throw redirect({ to: '/' });
    }
  },
  component: function SignupRoute() {
    return (
      <SignupView
        onSignup={async (input) => {
          const result = await signup(getApiClient(), input);
          useAuthStore.getState().setSession(result.session);
          // На onboarding пробрасываем widget_key первого сайта через
          // search-параметры — без отдельного state-store. После закрытия
          // вкладки и возврата onboarding читается из /api/v1/sites.
          await router.navigate({
            to: '/onboarding',
            search: { key: result.site.widgetKey },
          });
        }}
      />
    );
  },
});

// Onboarding-флоу — ANX-024 итерация B. Доступен только под auth, но
// без AppShell (Sidebar отвлекает): полная страница как у /login.
// search param `key` — widget_key первого сайта, прокидывается из /signup.
// Третий шаг ("получите первое сообщение") авто-чекается, когда поллер
// /conversations замечает хотя бы один диалог (ANX-024 итерация C).
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  beforeLoad: () => {
    if (useAuthStore.getState().session === null) {
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: (search: Record<string, unknown>) => ({
    key: typeof search.key === 'string' ? search.key : '',
  }),
  component: function OnboardingRoute() {
    const { key } = onboardingRoute.useSearch();
    const firstReceived = useFirstConversationPoll();
    return (
      <OnboardingView
        widgetKey={key}
        widgetSrc={getWidgetSrc()}
        firstMessageReceived={firstReceived}
      />
    );
  },
});

// ANX-013: forced enrollment. Если tenant policy require_mfa = true и
// оператор ещё не зарегистрировал passkey, /auth/login отдаёт
// enrollment_session вместо access. Router редиректит на эту страницу;
// она проводит enroll-flow и после успеха выдаёт session.
const forcedEnrollmentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/passkey-required',
  validateSearch: (search: Record<string, unknown>) => ({
    session: typeof search.session === 'string' ? search.session : '',
  }),
  component: function ForcedEnrollmentRoute() {
    const { session } = forcedEnrollmentRoute.useSearch();
    return (
      <ForcedEnrollmentView
        enrollmentSession={session}
        onCompleted={async () => {
          await router.navigate({ to: '/' });
        }}
      />
    );
  },
});

// useFirstConversationPoll — поллит GET /conversations каждые 3с пока не
// увидит первый conversation. WS-подписка на tenant-уровень потребовала бы
// нового room'а; polling-вариант проще и достаточен для onboarding flow.
function useFirstConversationPoll(): boolean {
  const { data } = useQuery({
    queryKey: ['onboarding_first_conv'],
    queryFn: () => listConversations(getApiClient(), { limit: 1 }),
    refetchInterval: (q) => {
      const items = q.state.data?.conversations;
      return items && items.length > 0 ? false : 3000;
    },
    refetchOnWindowFocus: true,
  });
  return !!data && data.conversations.length > 0;
}

// getWidgetSrc — URL для встраивания виджета. В dev/CI читаем из
// VITE_WIDGET_SRC; если не задан — defaults на текущий origin + /widget.js
// (gateway сам отдаёт виджет, см. ANX-024 итерация A).
function getWidgetSrc(): string {
  const env = (import.meta.env.VITE_WIDGET_SRC as string | undefined) ?? '';
  if (env !== '') return env;
  if (typeof window === 'undefined') return '/widget.js';
  return window.location.origin + '/widget.js';
}

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_protected',
  beforeLoad: () => {
    if (useAuthStore.getState().session === null) {
      throw redirect({ to: '/login' });
    }
  },
  component: AppShell,
});

const inboxLayoutRoute = createRoute({
  getParentRoute: () => protectedRoute,
  id: '_inbox',
  component: InboxLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => inboxLayoutRoute,
  path: '/',
  component: InboxEmpty,
});

const conversationRoute = createRoute({
  getParentRoute: () => inboxLayoutRoute,
  path: 'conversations/$id',
  component: function ConversationRoute() {
    const { id } = conversationRoute.useParams();
    return <ConversationView conversationId={id} />;
  },
});

const sitesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'sites',
  component: SitesPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'settings',
  component: SettingsPage,
});

const webhooksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'webhooks',
  component: WebhooksPage,
});

const analyticsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'analytics',
  component: function AnalyticsRoute() {
    const t = useT();
    return (
      <Suspense
        fallback={
          <p className="px-6 py-6 text-sm text-slate-500">{t('common.loading')}</p>
        }
      >
        <AnalyticsPage />
      </Suspense>
    );
  },
});

const cannedRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'canned-responses',
  component: CannedResponsesPage,
});

const exportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'exports',
  component: ExportsPage,
});

const auditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'audit',
  component: AuditLogPage,
});

const liveVisitorsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'live-visitors',
  component: LiveVisitorsPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'reports',
  component: ReportsPage,
});

// Страница «Тариф и оплата» (ANX-060 слой 4). search.status — возврат с оплаты
// банка (?status=paid|failed), редирект-URL формирует бэкенд (PanelURL/billing).
const billingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'billing',
  validateSearch: (search: Record<string, unknown>) => ({
    status: typeof search.status === 'string' ? search.status : undefined,
  }),
  component: function BillingRoute() {
    const { status } = billingRoute.useSearch();
    return <BillingPage returnStatus={status} />;
  },
});

// ANX-087 — staff-админка: отдельный контур (свой store/токен), вне
// операторского AppShell. /staff/login публичен; /staff/* под staff-гардом.
const staffLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/staff/login',
  beforeLoad: () => {
    if (useStaffAuthStore.getState().session !== null) {
      throw redirect({ to: '/staff' });
    }
  },
  component: function StaffLoginRoute() {
    return (
      <StaffLoginView
        onAuthenticated={async () => {
          await router.navigate({ to: '/staff' });
        }}
      />
    );
  },
});

const staffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/staff',
  beforeLoad: () => {
    if (useStaffAuthStore.getState().session === null) {
      throw redirect({ to: '/staff/login' });
    }
  },
  component: function StaffLayout() {
    return (
      <StaffShell>
        <Outlet />
      </StaffShell>
    );
  },
});

const staffIndexRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: '/',
  component: StaffDashboard,
});

const staffTenantsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: 'tenants',
  component: StaffTenantsPage,
});

const staffTenantDetailRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: 'tenants/$id',
  component: function StaffTenantDetailRoute() {
    const { id } = staffTenantDetailRoute.useParams();
    return <StaffTenantDetail id={id} />;
  },
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  signupRoute,
  onboardingRoute,
  forcedEnrollmentRoute,
  protectedRoute.addChildren([
    inboxLayoutRoute.addChildren([indexRoute, conversationRoute]),
    sitesRoute,
    settingsRoute,
    webhooksRoute,
    analyticsRoute,
    cannedRoute,
    exportsRoute,
    auditRoute,
    liveVisitorsRoute,
    reportsRoute,
    billingRoute,
  ]),
  staffLoginRoute,
  staffRoute.addChildren([staffIndexRoute, staffTenantsRoute, staffTenantDetailRoute]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
